/**
 * Строка программы обучения (training-program-picker; ТЗ 26.10.2026, п.4.3 и комментарий
 * заказчицы к п.4.5): первая строка — плашка вида документа и жирным «Краткое название · N ч»,
 * вторая — бледным официальное название и через точку полное название документа. «Инфо» — кто
 * подлежит обучению, периодичность, база (полностью), разряд, профстандарт, примечание. Одна и та
 * же строка — в выборе программ, в выбранных и на подтверждении.
 */
import { Fragment, type ReactNode } from 'react'
import { Info } from 'lucide-react'
import type { Program } from '@/lib/api'
import {
  DOCUMENT_KIND_LABELS,
  EDUCATION_BASE_INFO,
  catalogValue,
  documentFullName,
  documentKind,
  formatHours,
  formatRubles,
  periodInfoLabel,
  searchKey,
} from '@/lib/training-catalog'
import { cn } from '@/lib/utils'
import { DocumentTypeBadge } from '@/design-system/components/primitives/document-type-badge'
import { Popover, PopoverContent, PopoverTrigger } from '@/design-system/components/primitives/popover'

/** Поля программы, нужные строке и «Инфо»; индекс — где он известен (каталог, состав заказа). */
export type ProgramTitleData = Partial<Pick<Program, 'otIndex'>> & Pick<
  Program,
  | 'name'
  | 'shortName'
  | 'hours'
  | 'documentType'
  | 'audience'
  | 'recurrenceMode'
  | 'recurrenceMonths'
  | 'periodText'
  | 'educationBase'
  | 'rank'
  | 'profStandard'
  | 'note'
  | 'priceKopecks'
>

/** Подсветка совпадений поиска (от 2 символов) — регистр и «ё» не важны. */
export function Highlight({ text, query }: { text: string; query?: string }) {
  const q = searchKey((query ?? '').trim())
  if (q.length < 2) return <>{text}</>
  const key = searchKey(text)
  const parts: ReactNode[] = []
  let from = 0
  for (let i = key.indexOf(q); i >= 0; i = key.indexOf(q, from)) {
    if (i > from) parts.push(text.slice(from, i))
    parts.push(
      <mark key={i} className="rounded-7 bg-accent-bg text-inherit">
        {text.slice(i, i + q.length)}
      </mark>,
    )
    from = i + q.length
  }
  if (from === 0) return <>{text}</>
  parts.push(text.slice(from))
  return <>{parts.map((p, i) => (typeof p === 'string' ? <Fragment key={`t${i}`}>{p}</Fragment> : p))}</>
}

/** «Инфо» о программе — по клику (на телефоне — тапом), закрытие Esc / кликом вне / крестиком. */
export function ProgramInfoButton({ program, className }: { program: ProgramTitleData; className?: string }) {
  const title = program.shortName || program.name
  const rank = catalogValue(program.rank)
  const profStandard = catalogValue(program.profStandard)
  const rows: { label: string; value: ReactNode }[] = []
  if (program.audience.length > 0) {
    rows.push({
      label: 'Кто',
      value: (
        <ul className="list-disc pl-4">
          {program.audience.map((a) => (
            <li key={a}>{a}</li>
          ))}
        </ul>
      ),
    })
  }
  rows.push({ label: 'Периодичность', value: periodInfoLabel(program) })
  rows.push({ label: 'База', value: EDUCATION_BASE_INFO[program.educationBase] ?? '' })
  // Подпись колонки каталога заказчицы (ответ 27.10, В11).
  if (rank) rows.push({ label: 'Присваиваемый разряд', value: rank })
  if (profStandard) rows.push({ label: 'Профстандарт', value: profStandard })
  if (program.note.trim()) rows.push({ label: 'Примечание', value: <span className="whitespace-pre-line">{program.note}</span> })
  return (
    <Popover>
      {/* Подпись «Инфо» вместо значка «i» (ответ заказчицы 04.11 к п.4.1–4.3). */}
      <PopoverTrigger
        aria-label={`Подробнее о программе «${title}»`}
        className={cn(
          'ds-focus-ring inline-flex h-10 shrink-0 items-center gap-1 rounded-pill px-2 text-12 text-accent-txt transition-colors hover:bg-accent-bg nw:h-7',
          className,
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <Info className="size-3.5 stroke-current" aria-hidden />
        Инфо
      </PopoverTrigger>
      {/* Заголовок окна — «Детальное описание», как на картинке ТЗ (п.4.3); программа — первой
          строкой. Окно шире (ответ заказчицы 04.11): меньше переносов, больше видно без прокрутки. */}
      <PopoverContent title="Детальное описание" className="w-program-info max-w-narrow">
        <p className="px-4 pt-3 text-13 font-semibold text-txt">{title}</p>
        <dl className="grid grid-cols-1 gap-2 px-4 py-3 text-13">
          {rows.map((r) => (
            <div key={r.label}>
              <dt className="text-12 text-txt-tertiary">{r.label}</dt>
              <dd className="text-txt">{r.value}</dd>
            </div>
          ))}
        </dl>
      </PopoverContent>
    </Popover>
  )
}

/**
 * Строка программы: [плашка документа] **Краткое · N ч** (Инфо); ниже бледно — официальное
 * название (если отличается от краткого) · полное название документа. Длинные названия
 * переносятся, не обрезаются. `query` — подсветка совпадений поиска; `showPrice` — цена справа
 * бледно (только команде, ответ заказчицы 27.10, В10).
 */
export function ProgramTitle({
  program,
  query,
  info = true,
  showPrice = false,
  heading = false,
  inPackage = false,
}: {
  program: ProgramTitleData
  query?: string
  info?: boolean
  showPrice?: boolean
  /** Программа в составе пакета: жирным только название пакета (ответ заказчицы 04.11). */
  inPackage?: boolean
  /**
   * Краткое название с часами — заголовок третьего уровня (состав заказа); плашка документа и
   * «Инфо» — рядом, вне заголовка.
   */
  heading?: boolean
}) {
  const short = program.shortName || program.name
  const kind = documentKind(program.documentType)
  const hours = formatHours(program.hours)
  const official = program.name && program.name !== short ? program.name : ''
  // Полное название документа — если оно что-то добавляет к плашке («Удостоверение о повышении
  // квалификации»); «Протокол» и «Свидетельство» уже сказаны плашкой.
  const documentName = documentFullName(program.documentType)
  const fullDocument = kind && documentName === DOCUMENT_KIND_LABELS[kind] ? '' : documentName
  const price = showPrice ? formatRubles(program.priceKopecks) : ''
  return (
    <div className="flex min-w-0 items-start gap-2">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          {kind ? <DocumentTypeBadge kind={kind}>{DOCUMENT_KIND_LABELS[kind]}</DocumentTypeBadge> : null}
          {heading ? (
            <h3 className="min-w-0 break-words font-semibold text-txt">
              <Highlight text={short} query={query} />
              {hours ? <span className="whitespace-nowrap"> · {hours}</span> : null}
            </h3>
          ) : (
            <span className={cn('min-w-0 break-words text-txt', !inPackage && 'font-semibold')}>
              <Highlight text={short} query={query} />
              {hours ? <span className="whitespace-nowrap"> · {hours}</span> : null}
            </span>
          )}
          {/* Индекс программы (А, Б, В1, ПП, СИЗ…) — рядом с часами (ответ заказчицы 04.11). */}
          {program.otIndex ? (
            <span
              className="rounded-pill bg-gray-bg px-1.5 text-11 font-semibold text-txt-secondary"
              title="Индекс программы"
            >
              {program.otIndex}
            </span>
          ) : null}
        </div>
        {/* Полное наименование и документ — мельче (ответ заказчицы 04.11: «9 пунктов»). */}
        {official || fullDocument ? (
          <div className="mt-0.5 break-words text-11 text-txt-secondary">
            {official ? <Highlight text={official} query={query} /> : null}
            {official && fullDocument ? ' · ' : null}
            {fullDocument}
          </div>
        ) : null}
      </div>
      {price ? <span className="whitespace-nowrap pt-0.5 text-13 text-txt-tertiary">{price}</span> : null}
      {info ? <ProgramInfoButton program={program} className="-mt-1.5 nw:-mt-0.5" /> : null}
    </div>
  )
}
